import React, { useState, useEffect } from 'react'
import { 
  Table, 
  Button, 
  Space, 
  Modal, 
  Form, 
  Input, 
  Select, 
  message,
  Popconfirm,
  Card,
  Row,
  Col,
  Divider,
  Tree
} from 'antd'
import { 
  PlusOutlined, 
  EditOutlined, 
  DeleteOutlined,
  FolderOutlined,
  FolderOpenOutlined
} from '@ant-design/icons'
import { apiRequest } from '../../utils/api'

const { Option } = Select

function CategoryManagement() {
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(false)
  const [modalVisible, setModalVisible] = useState(false)
  const [editingCategory, setEditingCategory] = useState(null)
  const [form] = Form.useForm()
  
  // 获取分类列表
  useEffect(() => {
    fetchCategories()
  }, [])
  
  const fetchCategories = async () => {
    setLoading(true)
    try {
      const response = await apiRequest('/admin/categories')
      const data = await response.json()
      
      if (response.ok) {
        setCategories(data.categories || [])
      }
    } catch (error) {
      console.error('获取分类列表失败:', error)
      message.error('获取分类列表失败')
    } finally {
      setLoading(false)
    }
  }
  
  const handleAddCategory = () => {
    setEditingCategory(null)
    form.resetFields()
    setModalVisible(true)
  }
  
  const handleEditCategory = (category) => {
    setEditingCategory(category)
    form.setFieldsValue({
      name: category.name,
      parent_id: category.parent_id,
      description: category.description
    })
    setModalVisible(true)
  }
  
  const handleDeleteCategory = async (categoryId) => {
    try {
      const response = await apiRequest(`/admin/categories/${categoryId}`, {
        method: 'DELETE'
      })
      
      if (response.ok) {
        message.success('分类删除成功')
        fetchCategories()
      } else {
        const data = await response.json()
        message.error(data.error || '分类删除失败')
      }
    } catch (error) {
      console.error('删除分类失败:', error)
      message.error('删除分类失败')
    }
  }
  
  const handleModalOk = async () => {
    try {
      const values = await form.validateFields()
      
      const method = editingCategory ? 'PUT' : 'POST'
      const url = editingCategory 
        ? `/api/admin/categories/${editingCategory.id}` 
        : '/api/admin/categories'
      
      const response = await apiRequest(url, {
        method,
        body: JSON.stringify(values)
      })
      
      if (response.ok) {
        message.success(editingCategory ? '分类更新成功' : '分类添加成功')
        setModalVisible(false)
        form.resetFields()
        fetchCategories()
      } else {
        const data = await response.json()
        message.error(data.error || (editingCategory ? '分类更新失败' : '分类添加失败'))
      }
    } catch (error) {
      console.error('保存分类失败:', error)
      message.error('保存分类失败')
    }
  }
  
  // 构建树形结构数据
  const buildTreeData = (categories, parentId = null, level = 0) => {
    return categories
      .filter(category => category.parent_id === parentId)
      .map(category => {
        const children = buildTreeData(categories, category.id, level + 1)
        return {
          key: category.id,
          title: category.name,
          ...category,
          children: children.length > 0 ? children : undefined
        }
      })
  }
  
  // 构建父级分类选项
  const buildParentOptions = (categories, excludeId = null, level = 0) => {
    return categories
      .filter(category => category.id !== excludeId && (excludeId ? category.id !== excludeId : true))
      .map(category => {
        const indent = '　'.repeat(level)
        const option = (
          <Option key={category.id} value={category.id}>
            {indent}{category.name}
          </Option>
        )
        
        const children = buildParentOptions(
          categories.filter(c => c.parent_id === category.id), 
          excludeId, 
          level + 1
        )
        
        return [option, ...children]
      })
      .flat()
  }
  
  const treeData = buildTreeData(categories)
  
  const columns = [
    {
      title: '分类名称',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          {record.level === 0 ? <FolderOpenOutlined /> : <FolderOutlined />}
          <span>{text}</span>
        </Space>
      )
    },
    {
      title: '路径',
      dataIndex: 'path',
      key: 'path',
    },
    {
      title: '层级',
      dataIndex: 'level',
      key: 'level',
      render: (level) => `第${level + 1}级`
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '创建时间',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (date) => new Date(date).toLocaleDateString(),
    },
    {
      title: '操作',
      key: 'action',
      render: (_, record) => (
        <Space size="middle">
          <Button 
            type="primary" 
            icon={<EditOutlined />}
            size="small"
            onClick={() => handleEditCategory(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定删除这个分类吗？"
            description="删除分类将同时删除其子分类"
            onConfirm={() => handleDeleteCategory(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="primary" 
              danger 
              icon={<DeleteOutlined />}
              size="small"
              disabled={record.level === 0} // 禁止删除顶级分类
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ]
  
  return (
    <div className="category-management">
      <Card className="management-card">
        <div className="management-header">
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={handleAddCategory}
            >
              添加分类
            </Button>
          </Space>
        </div>
        
        <Divider />
        
        {/* 树形展示 */}
        <div style={{ marginBottom: 24 }}>
          <Tree
            className="category-tree"
            treeData={treeData}
            showIcon
            defaultExpandAll
            fieldNames={{ title: 'name', key: 'id' }}
          />
        </div>
        
        <Divider />
        
        {/* 表格展示 */}
        <Table
          dataSource={categories}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
          }}
          scroll={{ x: 'max-content' }}
        />
      </Card>
      
      {/* 添加/编辑分类模态框 */}
      <Modal
        title={editingCategory ? "编辑分类" : "添加分类"}
        open={modalVisible}
        onOk={handleModalOk}
        onCancel={() => {
          setModalVisible(false)
          form.resetFields()
        }}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="name"
                label="分类名称"
                rules={[{ required: true, message: '请输入分类名称' }]}
              >
                <Input placeholder="请输入分类名称" />
              </Form.Item>
            </Col>
            
            <Col span={12}>
              <Form.Item
                name="parent_id"
                label="父级分类"
              >
                <Select 
                  placeholder="请选择父级分类" 
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  <Option value={null}>顶级分类</Option>
                  {buildParentOptions(categories, editingCategory?.id)}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Row gutter={16}>
            <Col span={24}>
              <Form.Item
                name="description"
                label="分类描述"
              >
                <Input.TextArea 
                  placeholder="请输入分类描述" 
                  rows={3}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </div>
  )
}

export default CategoryManagement